import {createAsyncThunk} from '@reduxjs/toolkit';
import {BreadcrumbName} from 'common/src/modules/breadcrumbs/breadcrumbs.enums';
import {MovieLayer} from 'common/src/modules/movie/movieLayers.types';
import {LayerWithWorkingProps} from 'common/src/modules/movieEditor/movieEditor.types';
import {defaultLayerCategoryColor} from 'common/src/modules/movieEditor/movieEditor.consts';
import updateLayerIndex from 'common/src/modules/creativeTemplates/utils/updateLayerIndex';
import updateComps from './updateComps';
import {setActiveLayerIds} from '../formatMovies.slice';
import {RootState} from 'common/src/redux/store';
import {getActiveFormat} from 'common/src/modules/creativeTemplates/copilot/agents/adBuilderAgent/utils/getActiveFormat';
import getRandomString from 'common/src/utils/stringUtils/getRandomString';

interface AppendLayersParams {
  layers: MovieLayer[];
  insertIdx?: number;
  activeFormat?: string;
}

const appendLayers = createAsyncThunk(
  `${BreadcrumbName.AdBuilder}/appendLayers`,
  async (
    {layers, insertIdx = 1, activeFormat}: AppendLayersParams,
    {dispatch, getState},
  ): Promise<string[]> => {
    const state = getState() as RootState;
    const activeCompId = state.creativeTemplatePage.formatMovies.activeCompId;
    const workingFormat = activeFormat ?? getActiveFormat();

    const idxShift = layers.length;
    const layerIds = layers.map(() => getRandomString());
    const layersWithIds = layers.map((layer, index) => ({
      ...layer,
      id: layerIds[index],
    }));

    const getIndexShift = (i: number) => {
      if (i < Number(insertIdx)) return i;

      return i + idxShift;
    };

    const getIndexShiftFromCopied = (i: number) => {
      const existingIdx = layers.map((l) => +l.i).findIndex((t) => t === i);
      if (existingIdx !== -1) return +insertIdx + existingIdx;

      return getIndexShift(i);
    };

    await dispatch(
      updateComps({
        compsUpdate: (oldComps) => ({
          ...oldComps,
          [activeCompId]: {
            ...oldComps[activeCompId],
            l: [
              ...layersWithIds.map((layer, i) => ({
                ...updateLayerIndex(
                  {
                    ...layer,
                    isHidden: false,
                    isLocked: false,
                    categoryColor:
                      (layer as LayerWithWorkingProps).categoryColor || defaultLayerCategoryColor,
                  },
                  getIndexShiftFromCopied,
                ),
                i: (+insertIdx + i).toString(),
              })),
              ...oldComps[activeCompId].l.map((l) => updateLayerIndex(l, getIndexShift)),
            ],
          },
        }),
        activeFormat,
        replaceState: false,
      }),
    );

    // remove invalid parenting/trackmating
    await dispatch(
      updateComps({
        compsUpdate: (oldComps) => ({
          ...oldComps,
          [activeCompId]: {
            ...oldComps[activeCompId],
            l: oldComps[activeCompId].l.map((layer) =>
              updateLayerIndex(layer, (idx): number | undefined =>
                oldComps[activeCompId].l.some((l) => +l.i === idx) ? idx : undefined,
              ),
            ),
          },
        }),
        activeFormat,
        replaceState: true,
      }),
    );

    const updatedComps = (getState() as RootState).creativeTemplatePage.formatMovies.movies[
      workingFormat
    ];
    dispatch(
      setActiveLayerIds(
        layerIds.map((id) => updatedComps[activeCompId].l.find((l) => l.id === id)?.id || ''),
      ),
    );

    return layerIds;
  },
);

export default appendLayers;
